import type { PreviewRow, ReassignmentRowState } from './types';
import type { AnnotatorBatchBreakdown } from '../../types/response';

/** A row contributes to the allocation preview once From, To and a positive count are set. */
export const isActiveRow = (row: ReassignmentRowState): boolean =>
  Boolean(row.fromAnnotatorId && row.toAnnotatorId && row.count && row.count > 0);

/**
 * True when a row is missing From, To, or a positive count — not yet a
 * complete reassignment. A count of 0 moves nothing, so it counts as
 * incomplete rather than a valid (if trivial) move.
 */
export const isRowIncomplete = (row: ReassignmentRowState): boolean =>
  !row.fromAnnotatorId || !row.toAnnotatorId || row.count === null || row.count === 0;

const pendingOf = (perAnnotator: AnnotatorBatchBreakdown[], annotatorId: string): number =>
  perAnnotator.find((a) => a.annotatorId === annotatorId)?.pendingCount ?? 0;

/**
 * Maximum number of encounters this row may move.
 *
 * Trusts `row.movable` directly — it's already sibling-aware and
 * entity-accurate (see ReassignmentRow's `effectivePending` and the
 * backend's context-aware movable-count, both scoped to rows *before*
 * this one). Falls back to the plain pending count if not yet known.
 */
export const maxForRow = (
  row: ReassignmentRowState,
  perAnnotator: AnnotatorBatchBreakdown[]
): number => {
  if (row.movable != null) return Math.max(0, row.movable);
  return Math.max(0, pendingOf(perAnnotator, row.fromAnnotatorId));
};

/** True when the row's count exceeds what it is allowed to move (the red error state). */
export const rowHasError = (
  row: ReassignmentRowState,
  perAnnotator: AnnotatorBatchBreakdown[]
): boolean => {
  if (row.count == null) return false;
  if (row.count < 0) return true;
  if (!row.fromAnnotatorId) return false;
  return row.count > maxForRow(row, perAnnotator);
};

/** Net change in an annotator's pending count across all active rows. */
export const deltaForAnnotator = (rows: ReassignmentRowState[], annotatorId: string): number =>
  rows.filter(isActiveRow).reduce((delta, r) => {
    if (r.toAnnotatorId === annotatorId) return delta + (r.count ?? 0);
    if (r.fromAnnotatorId === annotatorId) return delta - (r.count ?? 0);
    return delta;
  }, 0);

const nameOf = (perAnnotator: AnnotatorBatchBreakdown[], id: string): string =>
  perAnnotator.find((a) => a.annotatorId === id)?.annotatorName ?? '';

export interface ActiveMove {
  count: number;
  fromName: string;
  toName: string;
}

/** The configured, complete moves for a batch, resolved to display names. */
export const activeMoves = (
  rows: ReassignmentRowState[],
  perAnnotator: AnnotatorBatchBreakdown[]
): ActiveMove[] =>
  rows.filter(isActiveRow).map((r) => ({
    count: r.count as number,
    fromName: nameOf(perAnnotator, r.fromAnnotatorId),
    toName: nameOf(perAnnotator, r.toAnnotatorId),
  }));

/** Build the preview-table rows for a batch from its breakdown + the configured rows. */
export const computePreviewRows = (
  perAnnotator: AnnotatorBatchBreakdown[],
  rows: ReassignmentRowState[]
): PreviewRow[] =>
  perAnnotator.map((a) => {
    const pendingBefore = a.pendingCount;
    const deltaPending = deltaForAnnotator(rows, a.annotatorId);
    const totalBefore = a.pendingCount + a.draftCount + a.finalisedCount;
    return {
      annotatorId: a.annotatorId,
      annotatorName: a.annotatorName,
      isAiAgent: Boolean(a.isAiAgent),
      pendingBefore,
      deltaPending,
      pendingAfter: pendingBefore + deltaPending,
      draftCount: a.draftCount,
      finalisedCount: a.finalisedCount,
      totalBefore,
      totalAfter: totalBefore + deltaPending,
    };
  });
